import { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { useTheme } from '../hooks/useTheme';
import { useMobile } from '../hooks/useWindowWidth';
import { usePageTitle } from '../hooks/usePageTitle';

// ─── Operator-supplied support details ───────────────────────────────────────
// Same rule as PrivacyPolicyPage's LEGAL block: the only values here that are
// not derivable from the code. Both stores require a Support URL that resolves
// to a page with a real way to reach a human — a 404 (or an SPA fallback that
// silently renders the landing page) is a rejection.
const SUPPORT = {
    email: 'support@kaziatlas.com',
    privacyEmail: 'privacy@kaziatlas.com',
    // Set expectations you can actually meet; an unmet promise here is worse
    // than a slower one.
    responseTime: 'within two business days',
};

const INK = 'var(--ink)';
const BODY = 'var(--ink-body)';
const MUTED = 'var(--ink-muted)';
const BORDER = '1px solid var(--border)';

const s: Record<string, CSSProperties> = {
    page: { background: 'var(--bg)', color: BODY, fontFamily: 'var(--font-body)', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
    nav: { borderBottom: BORDER, background: 'var(--surface)' },
    navInner: { maxWidth: 820, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem' },
    navHome: { background: 'none', border: 'none', color: MUTED, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', padding: 0 },
    main: { maxWidth: 820, margin: '0 auto', width: '100%', padding: '3rem 1.5rem 5rem', flex: 1 },
    h1: { fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.02em', color: INK, margin: '0 0 0.5rem' },
    lede: { fontSize: '1.05rem', lineHeight: 1.75, margin: '0 0 2.5rem' },
    h2: { fontSize: '1.35rem', fontWeight: 800, color: INK, margin: '2.5rem 0 0.75rem', letterSpacing: '-0.01em' },
    p: { lineHeight: 1.75, margin: '0 0 1rem' },
    ul: { lineHeight: 1.75, margin: '0 0 1rem', paddingLeft: '1.25rem' },
    link: { color: 'var(--accent-text)', textDecoration: 'none', fontWeight: 600 },
    footer: { borderTop: BORDER, background: 'var(--surface)', color: MUTED, fontSize: '0.8125rem', textAlign: 'center', padding: '1.5rem' },
};

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
    return (
        <section id={id}>
            <h2 style={s.h2}>{title}</h2>
            {children}
        </section>
    );
}

export function SupportPage() {
    usePageTitle('Support');
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isMobile = useMobile();

    return (
        <div style={s.page}>
            <nav style={s.nav}>
                <div style={{ ...s.navInner, padding: isMobile ? '1rem' : '1rem 1.5rem' }}>
                    <KaziAtlasLogo size={28} onLight={theme === 'light'} />
                    <button style={s.navHome} onClick={() => navigate('/')}>← Back to home</button>
                </div>
            </nav>

            <main style={s.main}>
                <h1 style={s.h1}>Support</h1>

                <p style={s.lede}>
                    Something broken, confusing, or missing? Email{' '}
                    <a href={`mailto:${SUPPORT.email}`} style={s.link}>{SUPPORT.email}</a>{' '}
                    and a human will reply {SUPPORT.responseTime}.
                </p>

                <Section id="faster" title="Getting a faster answer">
                    <p style={s.p}>
                        Include whatever of this you have — it usually turns two round trips
                        into one:
                    </p>
                    <ul style={s.ul}>
                        <li>What you expected to happen, and what happened instead</li>
                        <li>The project or task involved, and roughly when it happened</li>
                        <li>Whether you were on the web app, iOS, or Android — and the app version</li>
                        <li>A screenshot, if the problem is something you can see</li>
                    </ul>
                    <p style={s.p}>
                        The app version is shown on the sign-in screen and in Profile.
                    </p>
                </Section>

                <Section id="account" title="Account help">
                    <p style={s.p}>
                        Password resets are self-service from the sign-in screen — choose
                        “Forgot password” and follow the emailed link. If you cannot access the
                        email address on the account, write to us from any address and say which
                        account is affected; we will verify ownership another way rather than
                        emailing a reset to an address you no longer control.
                    </p>
                    <p style={s.p}>
                        If your workspace was created by an administrator, some settings —
                        including who can see which projects — are controlled by them rather
                        than by us, and we will point you to the right person.
                    </p>
                </Section>

                <Section id="privacy" title="Privacy and your data">
                    <p style={s.p}>
                        Deleting your account is self-service and does not need a support
                        request: Profile → Delete Account in the app, or our{' '}
                        <a href="/delete-account" style={s.link}>account deletion page</a> if
                        you no longer have it installed. It completes 30 days after you
                        confirm, and signing in before then cancels it.
                    </p>
                    <p style={s.p}>
                        For access, correction, or export requests — or a deletion you cannot
                        complete yourself — write to{' '}
                        <a href={`mailto:${SUPPORT.privacyEmail}`} style={s.link}>{SUPPORT.privacyEmail}</a>.
                        What we collect and why is set out in the{' '}
                        <a href="/privacy" style={s.link}>Privacy Policy</a>.
                    </p>
                </Section>

                <Section id="security" title="Reporting a security issue">
                    <p style={s.p}>
                        Please report suspected vulnerabilities to{' '}
                        <a href={`mailto:${SUPPORT.privacyEmail}`} style={s.link}>{SUPPORT.privacyEmail}</a>{' '}
                        rather than filing them publicly, and give us a reasonable window to fix
                        the issue before disclosing it. Include the steps to reproduce. We will
                        confirm receipt and tell you when it is resolved.
                    </p>
                </Section>
            </main>

            <footer style={s.footer}>
                <a href="/privacy" style={s.link}>Privacy Policy</a>
            </footer>
        </div>
    );
}
